import apiClient from '../client';
import { buildJsonApiQuery } from '@/utils/jsonapi';

/**
 * Partition Types API Module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */

/**
 * Fetch all partition types for current tenant
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { name: { icontains: 'service' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name', '-created_at'])
 * @param {Array<string>|string} [queryOptions.include] - Relations to include (e.g., ['values'])
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 * @returns {Promise<Object>} JSON:API response with data, meta, links
 */
export async function fetchPartitionTypes(queryOptions = {}) {
  // Explicit include keeps this request scoped to what the list page reads
  // (partition_values, for value counts) instead of falling through to the
  // interceptor's `?include=all` default, which walks every relationship the
  // serializer declares.
  const params = buildJsonApiQuery({ include: 'partition_values', ...queryOptions });
  const response = await apiClient.get('partition-types/', { params });
  return response.data;
}

/**
 * Fetch partition type detail with usage stats
 * @param {string} id - Partition type UUID
 * @returns {Promise<Object>} JSON:API response with partition type data
 */
export async function fetchPartitionTypeDetail(id) {
  const response = await apiClient.get(`partition-types/${id}/`);
  return response.data;
}

/**
 * Create a new partition type
 * @param {Object} data - Partition type data {name, description}
 * @returns {Promise<Object>} JSON:API response with created partition type
 */
export async function createPartitionType(data) {
  const payload = {
    data: {
      type: 'partition_type',
      attributes: data
    }
  };
  const response = await apiClient.post('partition-types/', payload);
  return response.data;
}

/**
 * Update an existing partition type
 * @param {string} id - Partition type UUID
 * @param {Object} data - Updated partition type data
 * @returns {Promise<Object>} JSON:API response with updated partition type
 */
export async function updatePartitionType(id, data) {
  const payload = {
    data: {
      type: 'partition_type',
      id,
      attributes: data
    }
  };
  const response = await apiClient.patch(`partition-types/${id}/`, payload);
  return response.data;
}

/**
 * Delete a partition type (only if not used by any metrics)
 * @param {string} id - Partition type UUID
 * @returns {Promise<void>}
 */
export async function deletePartitionType(id) {
  await apiClient.delete(`partition-types/${id}/`);
}

/**
 * Import partition values from a CSV file
 * @param {string} partitionTypeId - Partition type UUID
 * @param {File} file - CSV file with value and display_name columns
 * @returns {Promise<Object>} Import result {created, skipped, errors}
 */
export async function importPartitionValues(partitionTypeId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `partition-types/${partitionTypeId}/import/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { include: null },
    }
  );
  return response.data;
}

/**
 * Fetch metrics using a partition type (for usage validation)
 * @param {string} partitionTypeId - Partition type UUID
 * @returns {Promise<Object>} JSON:API response with metrics data
 */
export async function fetchMetricsByPartitionType(partitionTypeId) {
  const params = buildJsonApiQuery({
    filters: { partition_type: partitionTypeId },
    include: 'category,partition_configurations.partition_type'
  });
  const response = await apiClient.get('metrics/', { params });
  return response.data;
}
